'use client';

import { Button } from '@/components/button';
import { IconButton } from '@/components/iconButton';
import { isStoryblokEditorContext } from '@/helper/storyblok/isStoryblokEditorContext';
import RestartAltIcon from '@/public/icons/material/restart-alt.svg';
import ThumbDownOutlinedIcon from '@/public/icons/material/thumb-down-outlined.svg';
import ThumbDownIcon from '@/public/icons/material/thumb-down.svg';
import ThumbUpOutlinedIcon from '@/public/icons/material/thumb-up-outlined.svg';
import ThumbUpIcon from '@/public/icons/material/thumb-up.svg';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';

import classes from './FaqFeedback.module.scss';
import {
  type FaqResponse,
  countVotesFor,
  getRemoveAllVotesToken,
  removeVote,
  removeAllVotes as resetVotesForStory,
  saveDownVote,
  saveUpVote,
  updateVote,
} from './actions';

type FaqFeedbackType = {
  text?: string;
  className?: string;
  storyId: number;
  storySlug: string;
  dimmed?: boolean; // sets different background-color for light background (white)
};

type LocalFaqData = {
  id: string;
  vote: 1 | -1;
};

type VoteResult = 1 | -1 | undefined;

type VotingData = Record<string, LocalFaqData>;
const LOCAL_STORAGE_KEY = 'faq-votings';

export const FaqFeedback: React.FC<FaqFeedbackType> = ({ text, className, storyId, storySlug, dimmed = false }) => {
  const [hasVoted, setHasVoted] = useState(false);
  const [vote, setVote] = useState<VoteResult>();
  const [voteId, setVoteId] = useState<string | undefined>();
  const [isStoryblokSet, setIsStoryblokSet] = useState<boolean>(false);
  const [upVoteCount, setUpVoteCount] = useState<number | undefined>(undefined);
  const [downVoteCount, setDownVoteCount] = useState<number | undefined>(undefined);
  const storyKey = storyId.toString();

  const getLocalVotes = (): VotingData => JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');

  const getVotes = useCallback((key: string) => {
    const data = getLocalVotes();
    if (!data[key]) return;
    const { vote, id } = data[key];
    setHasVoted(true);
    setVote(vote);
    setVoteId(id);
  }, []); // or list any deps getLocalVotes, setHasVoted, etc.

  // Fetch and set aggregated counts for Storyblok preview/editor
  const fetchAndSetCounts = useCallback(async () => {
    try {
      const result = await countVotesFor(storyKey);
      setUpVoteCount(result.up || 0);
      setDownVoteCount(result.down || 0);
    } catch (e) {
      console.error('Error fetching faq result');
    }
  }, [storyKey]);

  function resetLocalStorage() {
    const data = getLocalVotes();
    for (const key in data) {
      if (data[key]?.id === voteId) {
        delete data[key];
        break;
      }
    }
    saveLocalVotes(data);

    // Set states
    setVote(undefined);
    setHasVoted(false);
  }

  useEffect(() => {
    setIsStoryblokSet(isStoryblokEditorContext({ includeDevelopment: true }));
  }, []);

  // Load poll result if in Storyblok UI
  useEffect(() => {
    if (isStoryblokSet) {
      void fetchAndSetCounts();
    }
  }, [isStoryblokSet, storyKey, fetchAndSetCounts]);

  useEffect(() => {
    getVotes(storyKey);
  }, [getVotes, storyKey]);

  const saveLocalVotes = (data: VotingData) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
  };

  const saveVote = async (responsePromise: Promise<FaqResponse>) => {
    const res = await responsePromise;
    if (!res?.success) return;

    const newVoteData: LocalFaqData = {
      id: res.insertId.toString(),
      vote: res.entry.vote,
    };

    const updatedVotes = getLocalVotes();
    updatedVotes[storyKey] = newVoteData;
    saveLocalVotes(updatedVotes);
    setVoteId(newVoteData.id);
  };

  // Update localStorage
  const updateLocalVote = (newVote: 1 | -1) => {
    const data = getLocalVotes();
    if (voteId) {
      for (const key in data) {
        if (data[key]?.id === voteId) {
          data[key].vote = newVote;
          break;
        }
      }
      saveLocalVotes(data);
    }
  };

  // Handle voting
  const handleVote = async (selectedVote: 1 | -1) => {
    if (hasVoted && voteId && vote === selectedVote) {
      // Unset vote in DB
      handleUnsetVote();
    } else if (hasVoted && voteId) {
      // Update vote in DB
      try {
        await updateVote(selectedVote, voteId);
      } catch (e) {
        console.error('Failed to update vote:', e);
      }

      updateLocalVote(selectedVote);
      setVote(selectedVote);
    } else {
      // Set vote in DB
      const voteFn = selectedVote === 1 ? saveUpVote : saveDownVote;
      saveVote(voteFn(storyId, storySlug));
      setVote(selectedVote);
    }

    setHasVoted(true);
  };

  // Unset user voting
  const handleUnsetVote = async () => {
    if (voteId) {
      try {
        await removeVote(voteId);
      } catch (e) {
        console.error('Failed to remove vote:', e);
      }
    }

    // Update localStorage
    resetLocalStorage();
  };

  const resetVote = async () => {
    if (storyKey) {
      try {
        // Retrieve short-lived admin token (editor-only) and pass it to the destructive action
        const token = await getRemoveAllVotesToken(storyKey);
        const resetResult = await resetVotesForStory(storyKey, token);
        if (resetResult >= 0) {
          // Update localStorage
          resetLocalStorage();
          // Refresh counts explicitly (Variant A)
          if (isStoryblokSet) {
            await fetchAndSetCounts();
          }
        }
      } catch (e) {
        console.error('Failed to reset votes:', e);
      }
    }
  };

  return (
    <div className={classNames(classes.faqFeedback, className, { [classes.dimmed]: dimmed })}>
      {text && <span className={classes.label}>{text}</span>}

      <span className={classes.iconButtonWrapper}>
        <IconButton
          className={classNames(
            classes.iconButton,
            {
              [classes.iconButtonSelected as string]: hasVoted && vote === 1,
            },
            'secondary'
          )}
          onClick={() => handleVote(1)}
          aria-label="Positiv bewerten"
          data-testid="faq-feedback-positive-button"
        >
          <ThumbUpOutlinedIcon />
          <ThumbUpIcon />
        </IconButton>
        {upVoteCount}
      </span>

      <span className={classes.iconButtonWrapper}>
        <IconButton
          data-testid="faq-feedback-negative-button"
          className={classNames(
            classes.iconButton,
            {
              [classes.iconButtonSelected as string]: hasVoted && vote === -1,
            },
            'secondary'
          )}
          onClick={() => handleVote(-1)}
          aria-label="Negativ bewerten"
        >
          <ThumbDownOutlinedIcon />
          <ThumbDownIcon />
        </IconButton>
        {downVoteCount}
      </span>
      {isStoryblokSet && (
        <Button variant="ctaFull" onClick={resetVote} startIcon={<RestartAltIcon />}>
          Reset
        </Button>
      )}
    </div>
  );
};

export default FaqFeedback;
